import { useSelector } from 'react-redux';

import { Button, Grid, IconButton } from '@mui/material';
import Add from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ClearIcon from '@mui/icons-material/Clear';

import { TextField } from 'mui-rff';
import { FieldArray } from 'react-final-form-arrays';

import { Typography } from '@components/styledComponents';
import MultiSelect from '@components/MultiSelect';

import { selectCurrencies } from '../../../../payment-options/store/payment-options-currencies';
import { selectSelectedOrchestrationRuleLimits } from '../../../store/payment-orchestration-rules.slice';

import { WEIGHTAGE_TYPE_LIST } from '../../../payment-orchestration.constants';
import {
  createEmptyWeightage,
  getPercentageTotal,
} from '../../../payment-orchestration.utils';

export default function WeightageForm({ isEdit = false, values, push }) {
  const currencies = useSelector(selectCurrencies);
  const selectedRuleLimits = useSelector(selectSelectedOrchestrationRuleLimits);

  const renderWeightageTotal = (currencyWeightage) => {
    const weightage = getPercentageTotal(currencyWeightage);
    const message = `Current total: ${weightage}%`;

    return (
      <>
        {weightage === 100 ? (
          <CheckCircleIcon style={{ color: 'green', display: 'inline' }} />
        ) : (
          <CancelIcon style={{ color: 'red', display: 'inline' }} />
        )}
        <Typography
          variant="body1"
          style={{
            marginTop: 0,
            marginLeft: 10,
            display: 'inline',
            verticalAlign: 'top',
          }}>
          {message}
        </Typography>
      </>
    );
  };

  if (isEdit && selectedRuleLimits) return null;

  return (
    <>
      <Typography
        className="form-header"
        variant="formHeader">
        Payment Currency Weightage
      </Typography>
      <Grid
        container
        alignItems="center"
        spacing={2}
        style={{ marginBottom: 20 }}>
        <Grid
          item
          xs={11}
          md={3}>
          <MultiSelect
            label="Weightage Type"
            name="weightageType"
            multiple={false}
            readOnly
            items={WEIGHTAGE_TYPE_LIST}
            optionDisplayKey="name"
            optionValueKey="value"
            value={values.weightageType}
            isOptionEqualToValue={(option, value) => option.value === value}
          />
        </Grid>
        <Grid
          item
          xs={1}
          md={2}
        />
        <Grid
          item
          xs={6}
          md={3}>
          {renderWeightageTotal(values.currencyWeightage)}
        </Grid>
        <Grid
          item
          xs={6}
          md={2}>
          <Button
            variant="outlined"
            disabled={isEdit}
            startIcon={<Add />}
            onClick={() => push('currencyWeightage', createEmptyWeightage())}>
            Add currency
          </Button>
        </Grid>
      </Grid>
      <FieldArray name="currencyWeightage">
        {({ fields }) =>
          fields.map((name, index) => (
            <Grid
              container
              alignItems="center"
              key={name}
              style={{ marginTop: 10 }}
              spacing={2}>
              <Grid
                item
                xs={5}>
                <MultiSelect
                  name={`${name}.currency`}
                  key={`${name}.currency`}
                  label="Select currency"
                  items={currencies.filter((item) => {
                    const selectedCurrencies = values.currencyWeightage.map(
                      (weight) => weight.currency,
                    );
                    const isSelected = selectedCurrencies.includes(item.code);
                    const isSelf =
                      values.currencyWeightage[index].currency === item.code;
                    return isSelf || !isSelected;
                  })}
                  multiple={false}
                  readOnly={isEdit}
                  optionDisplayKey="code"
                  optionValueKey="code"
                  value={values.currencyWeightage[index].currency}
                  isOptionEqualToValue={(option, value) =>
                    option.code === value
                  }
                  customOptionLabel={(option) =>
                    `${option.name} (${option.code})`
                  }
                  customFilterStringify={(option) =>
                    `${option.name} (${option.code})`
                  }
                />
              </Grid>
              <Grid
                item
                xs={3}>
                <TextField
                  label="Percentage (%)"
                  name={`${name}.percentage`}
                  key={`${name}.percentage`}
                  type="number"
                  InputProps={{
                    readOnly: isEdit,
                    inputProps: { min: 0, max: 100 },
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid
                item
                xs={2}>
                <IconButton
                  sx={{ color: 'secondary' }}
                  style={{
                    cursor: 'pointer',
                  }}
                  onClick={() => fields.remove(index)}
                  disabled={isEdit || fields.length === 1}
                  type="button">
                  <ClearIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))
        }
      </FieldArray>
    </>
  );
}
